import { fmtDateTimeGB, toIsoDate } from './dates';

export const TAB_KEYS = ['uploads', 'quality', 'consumption'];

export const dash = (v) =>
  v == null || (typeof v === 'string' && v.trim() === '') ? '—' : v;

export const fmtDT = (v) => (v ? fmtDateTimeGB(v) : '—');

export function deriveInitialTab(searchParams) {
  const v = searchParams.get('view');
  return TAB_KEYS.includes(v) ? v : 'uploads';
}

export function updateTabInSearch(params, setTab, nextTab, navigate, location) {
  setTab(nextTab);
  const next = new URLSearchParams(params);
  next.set('view', nextTab);

  navigate(`${location.pathname}?${next.toString()}`, {
    replace: true,
    state: location.state,
  });
}

export function computeStatusByDate(files = []) {
  return files.reduce((acc, f) => {
    const dateStr = f?.ReadingDate?.slice(0, 10);
    if (!dateStr) return acc;
    if (acc[dateStr] !== 'Loaded' && (f?.Status === 'Loaded' || f?.Status === 'Exported')) {
      acc[dateStr] = f.Status;
    } else if (!acc[dateStr]) {
      acc[dateStr] = f?.Status ?? 'Unknown';
    }
    return acc;
  }, {});
}

export function initFirstOfMonthUTC() {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
}

export function defaultRelativeRange(days = 20) {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - days);
  return { rangeStart: toIsoDate(start), rangeEnd: toIsoDate(end) };
}

// Chart label helpers (reused by quality/consumption charts)

export const monthFmt = new Intl.DateTimeFormat('en-GB', { month: 'short' });
export const yearFmt  = new Intl.DateTimeFormat('en-GB', { year: 'numeric' });
export const dayFmt   = new Intl.DateTimeFormat('en-GB', { day: 'numeric' });

export function monthYearFromDates(dates = []) {
  if (!dates.length) return '';
  const first = dates[0];
  const last  = dates[dates.length - 1];
  const sameMonth = first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear();
  if (sameMonth) return `${monthFmt.format(first)} ${yearFmt.format(first)}`;
  const sameYear = first.getFullYear() === last.getFullYear();
  if (sameYear) return `${monthFmt.format(first)}–${monthFmt.format(last)} ${yearFmt.format(first)}`;
  return `${monthFmt.format(first)} ${yearFmt.format(first)} – ${monthFmt.format(last)} ${yearFmt.format(last)}`;
}

export function fallbackRangeLabel(range) {
  const toLbl = (s) => (s ? new Date(s).toLocaleDateString('en-GB') : '');
  if (!range) return '';
  if (range.start && range.end) return `${toLbl(range.start)} – ${toLbl(range.end)}`;
  if (range.start) return `From ${toLbl(range.start)}`;
  if (range.end) return `To ${toLbl(range.end)}`;
  return '';
}

export const labelFormatterFromPayload = (_, payload) => {
  const p = payload && payload[0] && payload[0].payload;
  return p?.fullDate || '';
};

export const formatTooltipSingle = (v) => (v == null ? '—' : `${v} kWh`);
export const formatTooltipBoth = (v, name) => [
  v == null ? '—' : `${v} kWh`,
  name === 'ac' ? 'AC Energy' : 'DC Energy',
];

// Consumption chart helpers

export const METER_Y_DOMAIN = [0, 1000];
export const METER_Y_TICKS  = [0, 250, 500, 750, 1000];

export function buildEmptySeriesForRange(range, meterType) {
  if (!range?.start || !range?.end) return [];
  const out = [];
  const s = new Date(range.start);
  const e = new Date(range.end);
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    const label = d.toISOString().slice(0, 10);
    out.push(meterType === 3 ? { label, ac: null, dc: null } : { label, value: null });
  }
  return out;
}

export function toNumOrNull(v) {
  if (v == null) return null;
  if (typeof v === 'string' && v.toLowerCase() === 'pending') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function normalizeEnergyForChart(rows = [], meterType) {
  return (rows ?? []).map((r) => {
    const label = String(r.ReadingDate).slice(0, 10);
    const ac = toNumOrNull(r.ConsumptionAC);
    const dc = toNumOrNull(r.ConsumptionDC);
    if (meterType === 3) return { label, ac, dc };     
    if (meterType === 2) return { label, value: dc };  
    return { label, value: ac };                       
  });
}

// Quality chart helpers

const _safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function normalizeQualityPoint(d) {
  const t7  = _safeNumber(d?.Missing_Type7 ?? d?.MissingType7 ?? 0);
  const t9  = _safeNumber(d?.Missing_Type9 ?? d?.MissingType9 ?? d?.Missing ?? 0);
  const act = _safeNumber(d?.Actual);
  const unc = _safeNumber(d?.Uncertain);
  return { t7, t9, act, unc };
}

// Upload Calendar helpers

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const COLORS = {
  exported: '#5236AB',
  loaded:   '#9575CD',
  empty:    '#EA6361',
  today:    '#eaa861',
};

export function firstDayIndexUTC(year, month) {
  return (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7; // Monday=0
}

export function daysInUtcMonth(year, month) {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

export function isoForUTCDate(year, month, day) {
  return new Date(Date.UTC(year, month, day)).toISOString().slice(0, 10);
}

export function getColorForDay(iso, todayIso, statusByDate, theme) {
  if (iso === todayIso) return COLORS.today;
  if (iso < todayIso) {
    const s = statusByDate[iso];
    return s === 'Loaded'
      ? COLORS.loaded
      : s === 'Exported'
      ? COLORS.exported
      : COLORS.empty;
  }
  return theme.palette.action.disabledBackground;
}

export function getTitleForDay(iso, todayIso, statusByDate) {
  if (iso === todayIso) return 'Today';
  if (iso < todayIso) return statusByDate[iso] || 'No Data';
  return 'Future';
}

// Table helpers

export function monthNameUTC(date) {
  return date.toLocaleString('default', { month: 'long', year: 'numeric' });
}

export function dateToIsoUTC(d) {
  return new Date(d).toISOString().slice(0, 10);
}

export function isSameUtcMonth(dateLike, viewDate) {
  const d = new Date(dateLike);
  return d.getUTCFullYear() === viewDate.getUTCFullYear() &&
         d.getUTCMonth() === viewDate.getUTCMonth();
}

export function isIsoEqual(dateLike, iso) {
  if (!dateLike || !iso) return false;
  return dateToIsoUTC(dateLike) === iso;
}
